import React, { useState, useMemo } from 'react'
import { useAuth, useSettings } from '../App'
import { supabase } from '../lib/supabase'
import { BRANDS } from '../lib/constants'
import { exportToCSV, parseCSVImport } from '../lib/exportUtils'
import {
  getBaseLanded, getOffPriceWS, getOffPriceRRP, getOffPriceMargin,
  getStdWS, getStdRRP, getStdRRPexVAT, getStdMargin,
  getTRSupply, getTRRRP, getTRMargin,
  getD2CRRP, getD2CMarginNoPP, 
  getD2C10PctRRP, getD2C30PctRRP,
  getStockTotal, getStockStatus, getWeeksCover, getReorderQty,
  formatPct, CURRENCY_SYMBOLS
} from '../lib/pricing'

// ─── PRICING TAB ─────────────────────────────────────────────────────────────
export function PricingTab({ products }) {
  const { rolePerms } = useAuth()
  const { settings, updateSettings } = useSettings()
  const [currency, setCurrency] = useState('GBP')

  const sym = CURRENCY_SYMBOLS[currency] || '£'

  function mc(n) {
    if (n > 0.5) return 'margin-good'
    if (n > 0.3) return 'margin-warn'
    return 'margin-bad'
  }

  return (
    <div className="tab-content">
      <div className="tab-toolbar">
        <div style={{ fontSize: 14, fontWeight: 500 }}>Pricing matrix</div>
        <div className="currency-tabs">
          {['GBP', 'USD', 'EUR'].map(c => (
            <button key={c} className={`currency-tab ${currency === c ? 'active' : ''}`} onClick={() => setCurrency(c)}>
              {c} {CURRENCY_SYMBOLS[c]}
            </button>
          ))}
        </div>
      </div>

      <div className="info-box" style={{ marginBottom: 12 }}>
        Pricing chain: Factory Cost → Duty (×10%) → + Packaging + Freight = <strong>Landed USD</strong> ÷ FX =
        <strong> Landed {currency}</strong> → Off-Price (×2.5 WS / ×2.4 RRP) | Standard (×3 WS / ×14 RRP) |
        Travel Retail (Std RRP ×0.8 → rounded → ×35% supply) | D2C (Std RRP ±discounts)
      </div>

      {rolePerms.canEditPricing && (
        <div className="settings-row">
          <div className="setting-field">
            <label className="form-label">USD → GBP (÷)</label>
            <input className="form-input" type="number" step="0.01" value={settings.fx_usd_gbp}
              onChange={e => updateSettings('fx_usd_gbp', parseFloat(e.target.value))} style={{ width: 90 }} />
          </div>
          <div className="setting-field">
            <label className="form-label">USD → EUR (÷)</label>
            <input className="form-input" type="number" step="0.01" value={settings.fx_usd_eur}
              onChange={e => updateSettings('fx_usd_eur', parseFloat(e.target.value))} style={{ width: 90 }} />
          </div>
          <div className="setting-field">
            <label className="form-label">3PL Picking Fee £</label>
            <input className="form-input" type="number" step="0.01" value={settings.fee_3pl}
              onChange={e => updateSettings('fee_3pl', parseFloat(e.target.value))} style={{ width: 90 }} />
          </div>
          <div className="setting-field">
            <label className="form-label">P&P Fee £</label>
            <input className="form-input" type="number" step="0.01" value={settings.fee_pp}
              onChange={e => updateSettings('fee_pp', parseFloat(e.target.value))} style={{ width: 90 }} />
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto', marginTop: 12 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>SKU</th><th>Brand</th><th>Landed</th>
              <th>OP WS</th><th>OP RRP</th><th>OP Mgn</th>
              <th>Std WS</th><th>Std RRP</th><th>Std Ex VAT</th><th>Std Mgn</th>
              <th>TR Supply</th><th>TR RRP</th><th>TR Mgn</th>
              <th>D2C RRP</th><th>D2C Mgn</th>
              <th>10% off</th><th>30% off</th>
            </tr>
          </thead>
          <tbody>
            {products.map(p => {
              const l = getBaseLanded(p, currency, settings)
              return (
                <tr key={p.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>{p.sku}</td>
                  <td style={{ fontSize: 11 }}>{p.brand}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{sym}{l.toFixed(2)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{sym}{getOffPriceWS(p, currency, settings).toFixed(2)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 500 }}>{sym}{getOffPriceRRP(p, currency, settings)}</td>
                  <td className={mc(getOffPriceMargin(p, currency, settings))} style={{ fontSize: 11 }}>{formatPct(getOffPriceMargin(p, currency, settings))}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{sym}{getStdWS(p, currency, settings).toFixed(2)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 500 }}>{sym}{getStdRRP(p, currency, settings)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{sym}{getStdRRPexVAT(p, currency, settings).toFixed(2)}</td>
                  <td className={mc(getStdMargin(p, currency, settings))} style={{ fontSize: 11 }}>{formatPct(getStdMargin(p, currency, settings))}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{sym}{getTRSupply(p, currency, settings).toFixed(2)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 500 }}>{sym}{getTRRRP(p, currency, settings)}</td>
                  <td className={mc(getTRMargin(p, currency, settings))} style={{ fontSize: 11 }}>{formatPct(getTRMargin(p, currency, settings))}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 500 }}>{sym}{getD2CRRP(p, currency, settings)}</td>
                  <td className={mc(getD2CMarginNoPP(p, currency, settings))} style={{ fontSize: 11 }}>{formatPct(getD2CMarginNoPP(p, currency, settings))}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{sym}{getD2C10PctRRP(p, currency, settings).toFixed(2)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{sym}{getD2C30PctRRP(p, currency, settings).toFixed(2)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── STOCK TAB ───────────────────────────────────────────────────────────────
export function StockTab({ products, onRefresh }) {
  const [filterBrand, setFilterBrand] = useState('')
  const [filterAlert, setFilterAlert] = useState('')
  const [refreshing, setRefreshing] = useState(false)

  const filtered = useMemo(() => {
    let list = products
    if (filterBrand) list = list.filter(p => p.brand === filterBrand)
    if (filterAlert === 'out') list = list.filter(p => getStockTotal(p) === 0)
    else if (filterAlert === 'low') list = list.filter(p => getStockTotal(p) > 0 && getStockTotal(p) <= 10)
    else if (filterAlert === 'reorder') list = list.filter(p => { const wc = getWeeksCover(p); return wc !== null && wc <= 4 })
    return list
  }, [products, filterBrand, filterAlert])

  const ukTotal = products.reduce((s, p) => s + (parseInt(p.stock_uk) || 0), 0)
  const deTotal = products.reduce((s, p) => s + (parseInt(p.stock_de) || 0), 0)

  // In production this calls Flostream API
  async function handleRefresh() {
    setRefreshing(true)
    await new Promise(r => setTimeout(r, 1200))
    onRefresh()
    setRefreshing(false)
  }

  return (
    <div className="tab-content">
      <div className="tab-toolbar">
        <div style={{ fontSize: 14, fontWeight: 500 }}>Stock & forecast</div>
        <button className="btn" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : '↻ Refresh Flostream'}
        </button>
      </div>

      <div className="stock-locations">
        <div className="stock-loc">
          <div className="stock-loc-title"><span className="badge badge-uk">UK</span> Flostream UK · Corby</div>
          <div className="stock-loc-value">{ukTotal}</div>
          <div className="stock-loc-sub">units across all SKUs</div>
        </div>
        <div className="stock-loc">
          <div className="stock-loc-title"><span className="badge badge-de">DE</span> Flostream Germany · Frankfurt</div>
          <div className="stock-loc-value">{deTotal}</div>
          <div className="stock-loc-sub">units across all SKUs</div>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: 12 }}>
        <select className="btn" value={filterBrand} onChange={e => setFilterBrand(e.target.value)}>
          <option value="">All brands</option>
          {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select className="btn" value={filterAlert} onChange={e => setFilterAlert(e.target.value)}>
          <option value="">All stock levels</option>
          <option value="out">Out of stock</option>
          <option value="low">Low (≤10 units)</option>
          <option value="reorder">Needs reorder (≤4 wks cover)</option>
        </select>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>SKU</th><th>Brand</th><th>Product</th>
              <th>UK stock</th><th>DE stock</th><th>Total</th>
              <th>Avg wkly sales</th><th>Weeks cover</th><th>Reorder qty</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const total = getStockTotal(p)
              const wc = getWeeksCover(p)
              const status = getStockStatus(p)
              const rowClass = total === 0 ? 'row-out' : (wc !== null && wc <= 4) ? 'row-alert' : ''
              return (
                <tr key={p.id} className={rowClass}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>{p.sku}</td>
                  <td style={{ fontSize: 11 }}>{p.brand}</td>
                  <td style={{ fontSize: 11 }}>{(p.specification || p.product_type || '').substring(0, 30)}…</td>
                  <td><span className="badge badge-uk">{p.stock_uk || 0}</span></td>
                  <td><span className="badge badge-de">{p.stock_de || 0}</span></td>
                  <td style={{ fontWeight: 500 }}>{total}</td>
                  <td style={{ color: 'var(--color-text-secondary)', fontSize: 11 }}>{p.avg_weekly_sales || 2}/wk</td>
                  <td style={{ fontWeight: 500 }}>{wc !== null ? `${wc} wks` : '—'}</td>
                  <td style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{getReorderQty(p)} units</td>
                  <td><span className={`badge badge-${status.type}`}>{status.label}</span></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── IMPORT TAB ──────────────────────────────────────────────────────────────
export function ImportTab({ onImportComplete }) {
  const [importing, setImporting] = useState(false)
  const [preview, setPreview] = useState(null)
  const [message, setMessage] = useState('')

  async function handleFile(e) {
    const file = e.target.files[0]
    if (!file) return
    const text = await file.text()
    const parsed = parseCSVImport(text)
    setPreview(parsed)
    setMessage(`Ready to import ${parsed.length} products. Review below then confirm.`)
  }

 async function handleImport() {
    if (!preview || preview.length === 0) return
    setImporting(true)
    const deduplicated = preview.filter(
      (p, index, self) => index === self.findIndex(t => t.sku === p.sku)
    )
    try {
      const { error } = await supabase
        .from('products')
        .upsert(deduplicated, { onConflict: 'sku' })
      if (error) throw error
      setMessage('Successfully imported ' + deduplicated.length + ' products')
      setPreview(null)
      onImportComplete()
    } catch (err) {
      setMessage('Import failed: ' + err.message)
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="tab-content">
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>Import from Notion / CSV</div>
      <div className="info-box" style={{ marginBottom: 14 }}>
        Export your Notion database as CSV. All 74 fields from your W&J template are recognised automatically.
        Existing SKUs will be updated (upsert), new SKUs will be added. Photos can be included as URLs in the
        Packshot Image Link column.
      </div>

      <div className="drop-zone" onClick={() => document.getElementById('csv-file').click()}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>📄</div>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Drop CSV here or click to browse</div>
        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>Supports .csv exports from Notion, Excel, or Google Sheets</div>
        <input type="file" id="csv-file" accept=".csv" style={{ display: 'none' }} onChange={handleFile} />
      </div>

      {message && <div className="info-box" style={{ marginTop: 12 }}>{message}</div>}

      {preview && preview.length > 0 && (
        <>
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table className="data-table">
              <thead><tr><th>SKU</th><th>Brand</th><th>Product type</th><th>Factory cost</th><th>UK stock</th><th>DE stock</th></tr></thead>
              <tbody>
                {preview.slice(0, 10).map((p, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{p.sku}</td>
                    <td style={{ fontSize: 11 }}>{p.brand}</td>
                    <td style={{ fontSize: 11 }}>{p.product_type}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>${p.factory_cost}</td>
                    <td>{p.stock_uk}</td>
                    <td>{p.stock_de}</td>
                  </tr>
                ))}
                {preview.length > 10 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 8, color: 'var(--color-text-secondary)', fontSize: 12 }}>…and {preview.length - 10} more</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => { setPreview(null); setMessage('') }}>Cancel</button>
            <button className="btn primary" onClick={handleImport} disabled={importing}>
              {importing ? 'Importing…' : `Import ${preview.length} products`}
            </button>
          </div>
        </>
      )}

      <div style={{ fontSize: 13, fontWeight: 500, marginTop: 24, marginBottom: 8 }}>Notion photo sync</div>
      <div className="info-box">
        The hosted app automatically downloads Notion's temporary photo URLs and re-hosts them to permanent
        Supabase storage on first sync. You can also upload photos named by YVG SKU (e.g. WJW101.jpg) and
        they will be matched to products automatically.
      </div>
      <button className="btn" onClick={() => alert('Photo sync — configure Notion API token in settings')}>
        Configure Notion sync
      </button>
    </div>
  )
}

// ─── EXPORT TAB ──────────────────────────────────────────────────────────────
export function ExportTab({ products }) {
  const { settings } = useSettings()
  const [filterBrand, setFilterBrand] = useState('')
  const [currency, setCurrency] = useState('GBP')

  const filtered = filterBrand ? products.filter(p => p.brand === filterBrand) : products

  function handleExport() {
    const filename = `yvgportal-${filterBrand || 'all-brands'}`
    exportToCSV(filtered, currency, settings, filename)
  }

  return (
    <div className="tab-content">
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Export catalogue</div>

      <div className="toolbar" style={{ marginBottom: 14 }}>
        <select className="btn" value={filterBrand} onChange={e => setFilterBrand(e.target.value)}>
          <option value="">All brands</option>
          {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select className="btn" value={currency} onChange={e => setCurrency(e.target.value)}>
          <option value="GBP">GBP £</option>
          <option value="USD">USD $</option>
          <option value="EUR">EUR €</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{filtered.length} products</span>
      </div>

      <div className="export-option" onClick={handleExport}>
        <div style={{ fontSize: 22 }}>📁</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>CSV — all fields + all pricing channels</div>
          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>
            All 74 columns including Off-Price, Standard Wholesale, Travel Retail & D2C in {currency}
          </div>
        </div>
        <button className="btn primary">Export now</button>
      </div>

      <div className="export-option" onClick={() => alert('Excel with photos — available once hosted. Requires Supabase storage for photo embedding.')}>
        <div style={{ fontSize: 22 }}>📊</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Excel with reference photos</div>
          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>Embedded thumbnails + all pricing columns</div>
        </div>
        <button className="btn">Export</button>
      </div>

      <div className="export-option" onClick={() => alert('PDF lookbook — available in next release')}>
        <div style={{ fontSize: 22 }}>📔</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>PDF lookbook</div>
          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>Brand-grouped visual catalogue with photos, EANs, and all price points</div>
        </div>
        <button className="btn">Export</button>
      </div>

      <div className="export-option" onClick={() => alert('Xero sync — configure your Xero API credentials in settings')}>
        <div style={{ fontSize: 22 }}>🔗</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Sync to Xero</div>
          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>Push catalogue to Xero inventory · Pull sales data and ordering history</div>
        </div>
        <button className="btn">Connect Xero</button>
      </div>
    </div>
  )
}

// ─── TEAM TAB ────────────────────────────────────────────────────────────────
export function TeamTab() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)

  useMemo(async () => {
    try {
      const { data } = await supabase.from('user_roles').select('*, user:user_id(email)')
      setUsers(data || [])
    } catch { }
    setLoading(false)
  }, [])

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>Team & permissions</div>
        <button className="btn primary" onClick={() => alert('Invite via Supabase Auth — enter email and role')}>+ Invite member</button>
      </div>

      <table className="data-table" style={{ marginBottom: 24 }}>
        <thead><tr><th>Email</th><th>Role</th><th>Access</th><th></th></tr></thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: 'var(--color-text-secondary)' }}>Loading…</td></tr>
          ) : users.length === 0 ? (
            <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: 'var(--color-text-secondary)' }}>No team members yet</td></tr>
          ) : users.map(u => (
            <tr key={u.id}>
              <td style={{ fontSize: 12 }}>{u.user?.email}</td>
              <td><span className="badge" style={{ background: 'var(--color-background-secondary)' }}>{u.role}</span></td>
              <td style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                {u.role === 'admin' ? 'Full access' : u.role === 'editor' ? 'Add & edit products' : u.role === 'sales' ? 'Export only' : 'Read only'}
              </td>
              <td><button className="action-btn">Edit</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Role permissions</div>
      <table className="data-table">
        <thead><tr><th>Permission</th><th>Admin</th><th>Editor</th><th>Sales</th><th>Viewer</th></tr></thead>
        <tbody>
          {[
            ['View catalogue & pricing', true, true, true, true],
            ['Add / edit products', true, true, false, false],
            ['Delete products', true, false, false, false],
            ['Edit FX rates & multipliers', true, true, false, false],
            ['Import CSV / Notion sync', true, true, false, false],
            ['Export data', true, true, true, false],
            ['View stock & forecast', true, true, true, true],
            ['Manage team', true, false, false, false],
          ].map(([label, ...perms]) => (
            <tr key={label}>
              <td style={{ fontSize: 12 }}>{label}</td>
              {perms.map((has, i) => (
                <td key={i} style={{ fontSize: 12, color: has ? '#3B6D11' : 'var(--color-text-secondary)' }}>{has ? '✓' : '—'}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
